import { AfterViewInit, Directive, ElementRef, OnDestroy } from '@angular/core';

// This directive targets the goa-app-header component to address accessibility issues with its menu toggle button and icons.
// It ensures that the menu toggle button is hidden from screen readers and non-interactive, and that any icons without
// meaningful labels are also hidden from assistive technologies. It uses a MutationObserver to monitor changes in the DOM
// and reapply these fixes as needed, ensuring consistent accessibility improvements even if the header's content changes dynamically.
// Usage: Add the headerMenuAccessibility directive to any container that includes a goa-app-header element to enhance the accessibility
// of the header's menu and icons for screen reader users.
@Directive({
  selector: '[headerMenuAccessibility]',
  standalone: true,
})
export class HeaderMenuAccessibilityDirective
  implements AfterViewInit, OnDestroy
{
  private observer: MutationObserver | null = null;

  constructor(private el: ElementRef<HTMLElement>) {}

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.applyAccessibilityFixes();

      this.observer = new MutationObserver(() => {
        this.applyAccessibilityFixes();
      });

      this.observer.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class', 'aria-expanded', 'aria-controls', 'open'],
      });
    }, 0);
  }

  ngOnDestroy(): void {
    this.observer?.disconnect();
    this.observer = null;
  }

  private applyAccessibilityFixes(): void {
    const appHeader = this.getGoaAppHeader();
    if (!appHeader?.shadowRoot) {
      return;
    }

    const popoverTarget = this.querySelectorDeep<HTMLButtonElement>(
      appHeader.shadowRoot,
      'button.popover-target'
    );
    const menuToggleButton = this.querySelectorDeep<HTMLButtonElement>(
      appHeader.shadowRoot,
      'button[data-testid="menu-toggle"]'
    );

    if (
      popoverTarget &&
      menuToggleButton &&
      popoverTarget.contains(menuToggleButton)
    ) {
      menuToggleButton.setAttribute('aria-hidden', 'true');
      menuToggleButton.setAttribute('disabled', 'true');
      menuToggleButton.setAttribute('inert', '');
      menuToggleButton.style.pointerEvents = 'none';
    }

    const unlabeledIcons = this.querySelectorAllDeep(
      appHeader.shadowRoot,
      'goa-icon[aria-label=""], [role="img"][aria-label=""]'
    );

    unlabeledIcons.forEach((icon) => {
      icon.setAttribute('aria-hidden', 'true');
      icon.setAttribute('role', 'presentation');
    });

    const iconsWithInvalidAria = this.querySelectorAllDeep(
      appHeader.shadowRoot,
      '[role="img"][aria-expanded], [role="img"][aria-controls]'
    );

    iconsWithInvalidAria.forEach((icon) => {
      icon.removeAttribute('aria-expanded');
      icon.removeAttribute('aria-controls');
    });
  }

  private getGoaAppHeader():
    | (HTMLElement & { shadowRoot?: ShadowRoot })
    | null {
    const host = this.el.nativeElement;
    const withinHost = host.querySelector('goa-app-header') as
      | (HTMLElement & { shadowRoot?: ShadowRoot })
      | null;

    if (withinHost) {
      return withinHost;
    }

    return document.querySelector('goa-app-header') as
      | (HTMLElement & { shadowRoot?: ShadowRoot })
      | null;
  }

  private querySelectorDeep<T extends Element>(
    root: ShadowRoot | Document,
    selector: string
  ): T | null {
    const directMatch = root.querySelector(selector) as T | null;
    if (directMatch) {
      return directMatch;
    }

    const elements = Array.from(root.querySelectorAll('*'));
    for (const element of elements) {
      const host = element as HTMLElement & { shadowRoot?: ShadowRoot };
      if (host.shadowRoot) {
        const nested = this.querySelectorDeep<T>(host.shadowRoot, selector);
        if (nested) {
          return nested;
        }
      }
    }

    return null;
  }

  private querySelectorAllDeep(
    root: ShadowRoot | Document,
    selector: string
  ): Element[] {
    const matches = Array.from(root.querySelectorAll(selector));
    const elements = Array.from(root.querySelectorAll('*'));

    for (const element of elements) {
      const host = element as HTMLElement & { shadowRoot?: ShadowRoot };
      if (host.shadowRoot) {
        matches.push(...this.querySelectorAllDeep(host.shadowRoot, selector));
      }
    }

    return matches;
  }
}
